import { LocationTime } from "@/components/location-time";
import { Head, router } from "@inertiajs/react";
import { useTranslation } from "react-i18next";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  NativeSelect,
  NativeSelectOption,
} from "@/components/ui/native-select";
import SettingsLayout from "@/layouts/settings_layout";
import { userActivityPath } from "@/routes";

type Event = {
  id: string;
  summary: string;
  occurred_at: string;
  creator?: {
    first_name: string;
    last_name: string;
    email_address: string;
  } | null;
  location?: { name: string } | null;
  subject?: { label: string } | null;
};
type Option = {
  id: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  email_address?: string;
};

export default function Activity({
  events,
  filters,
  actions,
  members,
  locations,
  subject_types: subjectTypes,
  pagination,
}: {
  events: Event[];
  filters: Record<string, string>;
  actions: { value: string; label: string }[];
  members: Option[];
  locations: Option[];
  subject_types: string[];
  pagination: { page: number; pages: number; total: number };
}) {
  const { t, i18n } = useTranslation();
  const apply = (name: string, value: string | number) =>
    router.get(
      userActivityPath(),
      { ...filters, [name]: value, page: name === "page" ? value : 1 },
      { preserveState: true, replace: true },
    );
  return (
    <SettingsLayout>
      <Head title={t("common.activity")} />
      <div className="space-y-6">
        <header>
          <h1 className="text-xl font-semibold">{t("common.activity")}</h1>
          <p className="text-muted-foreground text-sm">
            {t("settings.activity_description")}
          </p>
        </header>
        <div className="grid gap-3 md:grid-cols-3">
          <NativeSelect
            aria-label={t("activity_ui.all_actors")}
            value={filters.actor_id || ""}
            onChange={(e) => apply("actor_id", e.target.value)}
          >
            <NativeSelectOption value="">
              {t("activity_ui.all_actors")}
            </NativeSelectOption>
            {members.map((item) => (
              <NativeSelectOption key={item.id} value={item.id}>
                {[item.first_name, item.last_name].filter(Boolean).join(" ") ||
                  item.email_address}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label={t("activity_ui.all_locations")}
            value={filters.location_id || ""}
            onChange={(e) => apply("location_id", e.target.value)}
          >
            <NativeSelectOption value="">
              {t("activity_ui.all_locations")}
            </NativeSelectOption>
            {locations.map((item) => (
              <NativeSelectOption key={item.id} value={item.id}>
                {item.name}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label={t("activity_ui.all_subjects")}
            value={filters.subject_type || ""}
            onChange={(e) => apply("subject_type", e.target.value)}
          >
            <NativeSelectOption value="">
              {t("activity_ui.all_subjects")}
            </NativeSelectOption>
            {subjectTypes.map((type) => (
              <NativeSelectOption key={type} value={type}>
                {t(`activity_ui.subjects.${type}`)}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <NativeSelect
            aria-label={t("activity_ui.action")}
            value={filters.event_action || ""}
            onChange={(e) => apply("event_action", e.target.value)}
          >
            <NativeSelectOption value="">
              {t("activity_ui.all_actions")}
            </NativeSelectOption>
            {actions.map((action) => (
              <NativeSelectOption key={action.value} value={action.value}>
                {action.label}
              </NativeSelectOption>
            ))}
          </NativeSelect>
          <div className="space-y-2">
            <Label htmlFor="activity-from">{t("activity_ui.from_date")}</Label>
            <Input
              id="activity-from"
              type="date"
              value={filters.from || ""}
              onChange={(e) => apply("from", e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="activity-to">{t("activity_ui.to_date")}</Label>
            <Input
              id="activity-to"
              type="date"
              value={filters.to || ""}
              onChange={(e) => apply("to", e.target.value)}
            />
          </div>
        </div>
        <Card>
          <CardHeader>
            <CardTitle>
              {t("common.activity")} ·{" "}
              {new Intl.NumberFormat(i18n.language).format(pagination.total)}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="divide-y">
              {events.map((event) => (
                <li key={event.id} className="py-4">
                  <p className="font-medium">{event.summary}</p>
                  <p className="text-muted-foreground text-sm">
                    {event.creator
                      ? [event.creator.first_name, event.creator.last_name]
                          .filter(Boolean)
                          .join(" ") || event.creator.email_address
                      : t("activity_ui.system")}
                    {event.location ? ` · ${event.location.name}` : ""}
                    {event.subject ? ` · ${event.subject.label}` : ""} ·{" "}
                    <LocationTime
                      value={event.occurred_at}
                      dateStyle="medium"
                      timeStyle="short"
                    />
                  </p>
                </li>
              ))}
            </ol>
            {events.length === 0 && (
              <p className="text-muted-foreground py-8 text-center">
                {t("activity_ui.empty")}
              </p>
            )}
            <div className="flex items-center justify-between pt-4">
              <button
                className="text-sm underline disabled:opacity-50"
                disabled={pagination.page <= 1}
                onClick={() => apply("page", pagination.page - 1)}
              >
                {t("activity_ui.previous")}
              </button>
              <span className="text-muted-foreground text-sm">
                {pagination.page} / {Math.max(pagination.pages, 1)}
              </span>
              <button
                className="text-sm underline disabled:opacity-50"
                disabled={pagination.page >= pagination.pages}
                onClick={() => apply("page", pagination.page + 1)}
              >
                {t("activity_ui.next")}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </SettingsLayout>
  );
}
